import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withState,
} from '@ngrx/signals';
import { isSignal } from '@angular/core';
import {
  entityConfig,
  setEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  ILoadingService,
  IToasterService,
  TLoadingRequest,
} from '@aksia/infrastructure';
import { finalize, Observable } from 'rxjs';

const TASK_STATE = {
  INITIAL: 'Initial',
  QUEUED: 'Queued',
  IN_PROGRESS: 'In Progress',
  FINALIZED: 'Finalized',
} as const;

type TTaskState = (typeof TASK_STATE)[keyof typeof TASK_STATE];

const TASK_EXECUTION_STRATEGY = {
  SEQUENTIAL: 'Sequential',
  PARALLEL: 'Parallel',
} as const;

type TTaskExecutionStrategy =
  (typeof TASK_EXECUTION_STRATEGY)[keyof typeof TASK_EXECUTION_STRATEGY];

const TASK_PRE_EXECUTION_RESULT = {
  CONTINUE: 'Continue',
  CONTINUE_WITH_NOTIFY: 'Continue with Notify',
  SKIP_TASK: 'Skip Task',
  SKIP_TASK_WITH_NOTIFY: 'Skip Task with Notify',
  SKIP_ALL_TASKS: 'Skip All Tasks',
  SKIP_ALL_TASKS_WITH_NOTIFY: 'Skip All Tasks with Notify',
} as const;

type TTaskPreExecutionStrategy =
  (typeof TASK_PRE_EXECUTION_RESULT)[keyof typeof TASK_PRE_EXECUTION_RESULT];

export type TTaskGroup = {
  id: string;
  executionStrategy: TTaskExecutionStrategy;
  tasks: Array<TTask>;
};

// Which request a release names. A key is not one request: it holds one per
// title, and more than one group can hold a title under the same key. o2 loads
// an entity and saves it under the same key, so a save finishing while a load
// was still in flight used to clear the loader that load was relying on,
// because a release that named only the key dropped every request under it.
// Naming the title releases what the task actually took.
//
// `addLoadRequest` falls back to the key when a task gives no title, so a
// release has to fall back the same way or it names nothing and leaves the
// request standing.
function titleOf(loadingOptions: { key: string; options: TLoadingRequest }) {
  return loadingOptions.options?.title ?? loadingOptions.key;
}

export type TTask = {
  id: string;
  state?: TTaskState;
  order?: number;
  method: (...args: any) => Observable<any>;
  arguments?: any[];
  loadingOptions?: { key: string; options: TLoadingRequest };
  notificationOptions?: {
    messageOnPreExecutionContinue?: string;
    messageOnPreExecutionSkip?: string;
    messageOnPreExecutionSkipAll?: string;
    messageOnSuccess?: string;
    messageOnError?: string;
    pinnedOnPreExecutionContinue?: boolean;
    pinnedOnPreExecutionSkip?: boolean;
    pinnedOnPreExecutionSkipAll?: boolean;
    pinnedOnSuccess?: boolean;
    pinnedOnError?: boolean;
    clickFnOnPreExecutionContinue?: () => void;
    clickFnOnPreExecutionSkip?: () => void;
    clickFnOnPreExecutionSkipAll?: () => void;
    clickFnOnSuccess?: () => void;
    clickFnOnError?: () => void;
  };
  preExecutionStrategy?: TTaskPreExecutionStrategy;
  onPreExecution?: () => TTaskPreExecutionStrategy | undefined;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
};

type TTaskOrchestrationsState = {
  allOrchestrationsAreFinalized: boolean;
};

const taskOrchestrationConfig = entityConfig({
  entity: type<TTaskGroup>(),
  collection: 'taskGroup',
  selectId: (taskGroup: TTaskGroup) => taskGroup.id!,
});

export function withTaskOrchestration<TaskOrchestrationFeature>() {
  return signalStoreFeature(
    {
      props: type<{
        loadingSvc?: ILoadingService;
        toasterSvc?: IToasterService;
      }>(),
    },
    withState<TTaskOrchestrationsState>({
      allOrchestrationsAreFinalized: false,
    }),
    withEntities(taskOrchestrationConfig),
    withMethods((store) => {
      return {
        registerTaskGroup(taskGroup: TTaskGroup) {
          taskGroup.tasks = taskGroup.tasks.map((task, index) => {
            task.state = TASK_STATE.INITIAL;
            if (task.loadingOptions) {
              store.loadingSvc?.registerLoadingKey(task.loadingOptions.key);
            }
            return task;
          });
          if (!store.taskGroupEntityMap()[taskGroup.id!]) {
            patchState(store, setEntity(taskGroup, taskOrchestrationConfig));
          } else {
            let existingTasks =
              store.taskGroupEntityMap()[taskGroup.id!].tasks ?? [];

            patchState(
              store,
              updateEntity(
                {
                  id: taskGroup.id!,
                  changes: {
                    tasks: [...existingTasks, ...taskGroup.tasks].toSorted(
                      (a, b) => (a?.order ?? 0) - (b?.order ?? 0),
                    ),
                  },
                },
                taskOrchestrationConfig,
              ),
            );
          }
        },

        executeTaskGroup(taskGroupId: string) {
          const taskGroup = store
            .taskGroupEntities()
            .find((taskGroup) => taskGroup.id === taskGroupId);

          if (!taskGroup) {
            throw new Error(`Task group with id ${taskGroupId} not found.`);
          }

          patchState(store, {
            allOrchestrationsAreFinalized: false,
          });

          taskGroup.tasks.forEach((task) => {
            task.state = TASK_STATE.QUEUED;
          });

          if (
            taskGroup.executionStrategy === TASK_EXECUTION_STRATEGY.SEQUENTIAL
          ) {
            this.executeTasksSequentially(taskGroup);
          } else {
            this.executeTasksInParallel(taskGroup);
          }
        },

        executeTasksSequentially(taskGroup: TTaskGroup) {
          const executeNextTask = () => {
            let nextQueuedTask = taskGroup.tasks.find(
              (task) => task.state === TASK_STATE.QUEUED,
            );

            if (nextQueuedTask) {
              nextQueuedTask.state = TASK_STATE.IN_PROGRESS;
              if (nextQueuedTask.onPreExecution) {
                const preExecutionGuard = nextQueuedTask.onPreExecution();

                switch (preExecutionGuard) {
                  case TASK_PRE_EXECUTION_RESULT.CONTINUE:
                    if (nextQueuedTask.loadingOptions) {
                      store.loadingSvc?.addLoadRequest(
                        nextQueuedTask.loadingOptions.key,
                        nextQueuedTask.loadingOptions.options,
                      );
                    }
                    break;
                  case TASK_PRE_EXECUTION_RESULT.CONTINUE_WITH_NOTIFY:
                    if (
                      nextQueuedTask.notificationOptions
                        ?.messageOnPreExecutionContinue
                    ) {
                      store.toasterSvc?.error(
                        nextQueuedTask.notificationOptions
                          ?.messageOnPreExecutionContinue ??
                          'Task pre-execution condition passed with errors, continuing execution.',
                        {
                          pinned:
                            nextQueuedTask.notificationOptions
                              ?.pinnedOnPreExecutionContinue,
                          clickFn:
                            nextQueuedTask.notificationOptions
                              ?.clickFnOnPreExecutionContinue,
                        },
                      );
                    }

                    break;
                  case TASK_PRE_EXECUTION_RESULT.SKIP_TASK:
                    nextQueuedTask.state = TASK_STATE.FINALIZED;
                    executeNextTask();
                    break;
                  case TASK_PRE_EXECUTION_RESULT.SKIP_TASK_WITH_NOTIFY:
                    if (
                      nextQueuedTask.notificationOptions
                        ?.messageOnPreExecutionSkip
                    ) {
                      store.toasterSvc?.error(
                        nextQueuedTask.notificationOptions
                          ?.messageOnPreExecutionSkip ??
                          'Task pre-execution condition failed, skipping execution.',
                        {
                          pinned:
                            nextQueuedTask.notificationOptions
                              ?.pinnedOnPreExecutionSkip,
                          clickFn:
                            nextQueuedTask.notificationOptions
                              ?.clickFnOnPreExecutionSkip,
                        },
                      );
                    }
                    nextQueuedTask.state = TASK_STATE.FINALIZED;
                    executeNextTask();
                    break;
                  case TASK_PRE_EXECUTION_RESULT.SKIP_ALL_TASKS:
                    taskGroup.tasks.forEach((task) => {
                      task.state = TASK_STATE.FINALIZED;
                    });
                    patchState(store, {
                      allOrchestrationsAreFinalized: true,
                    });
                    break;
                  case TASK_PRE_EXECUTION_RESULT.SKIP_ALL_TASKS_WITH_NOTIFY:
                    if (
                      nextQueuedTask.notificationOptions
                        ?.messageOnPreExecutionSkip
                    ) {
                      store.toasterSvc?.error(
                        nextQueuedTask.notificationOptions
                          ?.messageOnPreExecutionSkip ??
                          'Task pre-execution condition failed, skipping all executions.',
                        {
                          pinned:
                            nextQueuedTask.notificationOptions
                              ?.pinnedOnPreExecutionSkipAll,
                          clickFn:
                            nextQueuedTask.notificationOptions
                              ?.clickFnOnPreExecutionSkipAll,
                        },
                      );
                    }
                    taskGroup.tasks.forEach((task) => {
                      task.state = TASK_STATE.FINALIZED;
                    });
                    patchState(store, {
                      allOrchestrationsAreFinalized: true,
                    });
                    break;
                }
              }

              if (nextQueuedTask.state === TASK_STATE.IN_PROGRESS) {
                if (nextQueuedTask.loadingOptions) {
                  store.loadingSvc?.addLoadRequest(
                    nextQueuedTask.loadingOptions.key,
                    nextQueuedTask.loadingOptions.options,
                  );
                }

                let args =
                  nextQueuedTask.arguments?.map((arg) =>
                    isSignal(arg) || typeof arg === 'function' ? arg() : arg,
                  ) ?? [];

                nextQueuedTask
                  .method(...args)
                  .pipe(
                    finalize(() => {
                      nextQueuedTask.state = TASK_STATE.FINALIZED;
                      executeNextTask();
                    }),
                  )
                  .subscribe({
                    next: (response) => {
                      if (nextQueuedTask.onSuccess) {
                        nextQueuedTask.onSuccess(response);
                      }

                      if (nextQueuedTask.loadingOptions) {
                        store.loadingSvc?.setLoadResponse(
                          nextQueuedTask.loadingOptions.key,
                          titleOf(nextQueuedTask.loadingOptions),
                        );
                      }

                      if (
                        nextQueuedTask.notificationOptions?.messageOnSuccess
                      ) {
                        store.toasterSvc?.success(
                          nextQueuedTask.notificationOptions.messageOnSuccess,
                          {
                            pinned:
                              nextQueuedTask.notificationOptions
                                ?.pinnedOnSuccess,
                            clickFn:
                              nextQueuedTask.notificationOptions
                                ?.clickFnOnSuccess,
                          },
                        );
                      }
                    },
                    error: (error) => {
                      if (nextQueuedTask.onError) {
                        nextQueuedTask.onError(error);
                      }

                      if (nextQueuedTask.loadingOptions) {
                        store.loadingSvc?.setLoadResponse(
                          nextQueuedTask.loadingOptions.key,
                          titleOf(nextQueuedTask.loadingOptions),
                        );
                      }
                      if (nextQueuedTask.notificationOptions?.messageOnError) {
                        store.toasterSvc?.error(
                          nextQueuedTask.notificationOptions.messageOnError,
                          {
                            pinned:
                              nextQueuedTask.notificationOptions?.pinnedOnError,
                            clickFn:
                              nextQueuedTask.notificationOptions
                                ?.clickFnOnError,
                          },
                        );
                      }
                    },
                  });
              }
            } else {
              // Backstop. A task releases its own request when it emits or
              // errors, and nothing releases one whose method completes without
              // doing either — `finalize` only advances the queue. While a
              // release dropped the whole key that could not strand anything,
              // because the next task through cleared it; now that a release
              // names its title, a stranded request would hold its loader up
              // for good. Once the group has no queued task left, none of its
              // own requests can still be in flight.
              //
              // It sweeps by title rather than by key, for the same reason the
              // release does: a key is shared, and the group that loads an
              // entity holds one while the group that saves it finishes. A
              // release that names a title nothing holds is a no-op, so
              // sweeping what already went is free.
              for (const finished of taskGroup.tasks) {
                if (!finished.loadingOptions) continue;
                store.loadingSvc?.setLoadResponse(
                  finished.loadingOptions.key,
                  titleOf(finished.loadingOptions),
                );
              }
              patchState(store, {
                allOrchestrationsAreFinalized: true,
              });
            }
          };

          executeNextTask();
        },

        executeTasksInParallel(taskGroup: TTaskGroup) {
          taskGroup.tasks.forEach((task) => {
            task.state = TASK_STATE.IN_PROGRESS;
            let args =
              task.arguments?.map((arg) => (isSignal(arg) ? arg() : arg)) ?? [];

            task.method(...args).subscribe({
              next: (response) => {
                if (task.onSuccess) {
                  task.onSuccess(response);
                }

                if (task.notificationOptions?.messageOnSuccess) {
                  store.toasterSvc?.success(
                    task.notificationOptions.messageOnSuccess,
                  );
                }
              },
              error: (error) => {
                if (task.notificationOptions?.messageOnError) {
                  store.toasterSvc?.error(
                    task.notificationOptions.messageOnError,
                  );
                }

                if (task.onError) {
                  task.onError(error);
                }
              },
              complete: () => {
                task.state = TASK_STATE.FINALIZED;
              },
            });
          });
        },
      };
    }),
  );
}
